<template>
	<div>
		<div class="flex wrap xs-12 xs-center xs-top files-upload-full-height">
			<!-- Display files to upload -->
			<div class="flex xs-12 bg5" v-if="files.length > 0">
				<j-data-table
					:header="[
						{ text: $t('file') },
						{ text: $t('size') },
						{ text: $t('status') },
						...(showPrivacySelect ? [{ text: $t('privacy_level') }] : []),
						{ text: $t('remove_file') }
					]"
					:list="files"
					:isLoading="false"
				>
					<template slot="list" slot-scope="{ item }">
						<td>{{ item.name }}</td>
						<td>{{ item.size | fileSize }}</td>
						<td>
							<span v-if="item.error" class="fw7 cError">
								{{ $t(`errors.file_${item.error}`) }}
							</span>
							<span
								v-else-if="item.success"
								class="bgSuccess--l5 cSuccess--d4 p1 r2 fw7"
								>{{ $t("success") }}</span
							>
							<span v-else-if="item.active" class="fw7">{{
								$t("uploading")
							}}</span>
						</td>
						<td v-if="showPrivacySelect">
							<j-base-select
								size="xsmall"
								value="private"
								@input="onInputPrivacy(item, $event)"
								:allowSelectNothing="false"
								:options="[
									{ name: 'Public', value: 'public' },
									{ name: 'Private', value: 'private' }
								]"
							/>
						</td>
						<td>
							<!-- Show delete only if file is not being uploaded or was already uploaded -->
							<div class="action" v-if="!item.success && !item.active">
								<crm-icon
									name="delete"
									size="normal"
									color="cError"
									@click.native.prevent="removeFile(item)"
								/>
							</div>
						</td>
					</template>
					<template slot="empty">{{ $t("no_data") }}</template>
				</j-data-table>
			</div>

			<!-- Upload Button -->
			<vue-upload-component
				class="flex xs-12 xs-center p2 bg5"
				ref="upload"
				v-model="files"
				:post-action="getUrl"
				:headers="headers"
				:data="getBody"
				:drop="true"
				:drop-directory="false"
				:multiple="true"
				:maximum="maxNumberOfFiles"
				:extensions="
					supportedFileTypes
						? supportedFileTypes
						: $store.state.settings.filesUpload.types
				"
				:size="$store.state.settings.filesUpload.maxFileSize"
				@input-file="inputFile"
				@input-filter="inputFilter"
			>
				<!-- Show when there are no files -->
				<template v-if="files.length === 0">
					<div class="flex wrap xs-12 xs-center b2 p15 bc7">
						<j-text class="xs-12 fs5 c6 fw7 mb8">
							{{ $t("drag_your_files") }}
						</j-text>
						<j-button type="bordered" size="medium">
							{{ $t("select_files") }}
						</j-button>
					</div>
				</template>

				<!-- Show when there are some files already -->
				<div
					class="flex xs-left pt2"
					v-if="files.length > 0 && files.length < maxNumberOfFiles"
				>
					<j-button type="tertiary">
						<crm-icon name="add" size="small" color="c2" />
						{{ $t("select_more_files") }}
					</j-button>
				</div>
			</vue-upload-component>

			<!-- Upload button -->
			<div class="flex xs-center xs-12 p2 bg5 bt1 bc7" v-if="files.length > 0">
				<j-button
					type="primary"
					:isLoading="$refs.upload && $refs.upload.active"
					@click="onSubmit"
					>{{ $t("upload_files") }}</j-button
				>
			</div>
		</div>
	</div>
</template>

<script>
import CrmIcon from "@/components/CrmIcon/"
import JText from "@/components/JText/"
import JButton from "@/components/JButton/"
import JBaseSelect from "@/components/JBaseSelect/"
import JDataTable from "@/components/JDataTable/"

// We use 3rd party component for upload:
// https://lian-yue.github.io/vue-upload-component/#/en/documents
import VueUploadComponent from "vue-upload-component"

/**
 * This component is resposible for uploading of files
 * We can attach files into many things: client, policy...
 * When upload is finished, component emits event "upload:finished"
 */
export default {
	components: {
		JText,
		JButton,
		JBaseSelect,
		CrmIcon,
		JDataTable,
		VueUploadComponent
	},

	props: {
		/**
		 * Where to upload files
		 * This determnies endpoint to use for file upload
		 */
		filesOf: {
			validator(val) {
				return (
					val === "entity" ||
					val === "policy" ||
					val === "claim" ||
					val === "template" ||
					val === "templateAssets" ||
					val === "case" ||
					val === "proposals" ||
					val === "custom"
				)
			},
			required: true
		},

		/*
		 * Only for filesOf 'custom'
		 * Used in file system module to add file in any endpoint
		 */
		customPath: {
			type: String,
			required: false,
			default: "test"
		},

		/*
		 * Allow to choose between public or private upload
		 */
		showPrivacySelect: {
			type: Boolean,
			required: false,
			default: false
		},

		/**
		 * Id of client, policy... we are uploading file to
		 */
		id: {
			type: String,
			default: ""
		},

		// optional
		supportedFileTypes: {
			type: Array,
			required: false
		}
	},

	data() {
		return {
			// Mas number of files to upload
			maxNumberOfFiles: 10,

			// List of files to upload
			files: [],

			// Auth
			headers: {
				Authorization: `Bearer ${window.TK}`
			}
		}
	},

	computed: {
		/**
		 * Return proper endpoint for upload based on filesOf
		 */
		getUrl() {
			const claimsPath = encodeURIComponent(`claims/${this.id}`) // new way of uploading files
			const templateAssets = encodeURIComponent(`templates/assets`)

			if (this.filesOf === "entity")
				return `https://api.asia.covergo.com/api/v1/files/${encodeURIComponent(
					`entities/${this.id}/attachments`
				)}`
			else if (this.filesOf === "claim")
				return `https://api.asia.covergo.com/api/v1/files/${claimsPath}`
			else if (this.filesOf === "policy")
				return `https://api.asia.covergo.com/api/v1/files/policies/${this.id}/attachments`
			else if (this.filesOf === "template")
				return `https://api.asia.covergo.com/api/v1/files/templates/`
			else if (this.filesOf === "templateAssets")
				return `https://api.asia.covergo.com/api/v1/files/${templateAssets}`
			else if (this.filesOf === "case")
				return `https://api.asia.covergo.com/api/v1/files/${encodeURIComponent(
					`case/${this.id}`
				)}`
			else if (this.filesOf === "proposals")
				return `https://api.asia.covergo.com/api/v1/files/${encodeURIComponent(
					`proposals/${this.id}`
				)}`
			else if (this.filesOf === "custom")
				return `https://api.asia.covergo.com/api/v1/files/${this.customPath}`
			return ""
		},

		getBody() {
			if (this.filesOf === "templateAssets") {
				return {
					isPublic: true
				}
			} else return null
		}
	},

	methods: {
		/**
		 * Will trigger upload of files
		 */
		onSubmit() {
			this.$refs.upload.active = true
		},

		/*
		 * Set isPublic param depending on the selected value
		 */
		onInputPrivacy(item, input) {
			if (item.data) item.data.isPublic = input === "public"
			else item.data = { isPublic: input === "public" }
		},

		/**
		 * Upload files
		 */
		inputFile: function(newFile, oldFile) {
			if (newFile && oldFile && !newFile.active && oldFile.active) {
				// Get response data
				if (newFile.xhr) {
					if (newFile.xhr.status === 401) {
						// If 401 - show message that the user is not authorized to do this action
						this.$store.dispatch(
							"addMessage",
							{
								message: {
									type: "error",
									text: this.$t("errors.authorization")
								}
							},
							{ root: true }
						)
					} else if (newFile.xhr.status > 401) {
						// If different error, show general error
						this.$store.dispatch(
							"addMessage",
							{
								message: {
									type: "error",
									text: this.$t("errors.general")
								}
							},
							{ root: true }
						)
					} else {
						// Check if file already exists
						const res = JSON.parse(newFile.xhr.response)

						// If file already exists in the backend, show error
						if (
							res.errors &&
							res.errors.includes(
								"Specified Object Already Exists in the Bucket"
							)
						) {
							this.$refs.upload.update(newFile, {
								error: "already_exists"
							})
						}
					}
				}
			}
		},

		/**
		 * Filter out some specific files like system files and hidden files
		 */
		inputFilter: function(newFile, oldFile, prevent) {
			if (newFile && !oldFile) {
				// Filter system files or hidden files
				if (/(\/|^)(Thumbs\.db|desktop\.ini|\..+)$/.test(newFile.name)) {
					return prevent()
				}
			}
		},

		removeFile(file) {
			this.$refs.upload.remove(file)
		}
	},

	/**
	 * Here we have to set a special watcher that watches $refs
	 * $refs is not available before mount, so this has to happen in mount
	 * The reason is file component is returning this.$refs.upload.uploaded = true
	 * when all uploads finsihed, we need to catch that and emit event to parent
	 */
	mounted() {
		this.$watch(
			() => {
				return this.$refs.upload && this.$refs.upload.uploaded
			},
			val => {
				if (val === true) this.$emit("upload:finished")
			}
		)
	},

	filters: {
		/**
		 * Show proper file size
		 */
		fileSize(a, b) {
			if (0 == a) return "0 Bytes"
			let c = 1024,
				d = b || 2,
				e = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"],
				f = Math.floor(Math.log(a) / Math.log(c))
			return parseFloat((a / Math.pow(c, f)).toFixed(d)) + " " + e[f]
		}
	}
}
</script>

<style scoped>
.files-upload-full-height {
	min-height: 100%;
}
</style>
